import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Box, CssBaseline, ThemeProvider } from '@mui/material';

import moment from 'moment';
import 'moment/dist/locale/ar';
import 'moment/dist/locale/de';
import 'moment/dist/locale/it';

import { AdminLayout } from './components/admin/AdminLayout';
import { LoadingSpinner } from './components/loading/LoadingSpinner';
import { PaywallModal } from './components/modals/PaywallModal';
import { TutorialsModal } from './components/modals/TutorialsModal';
import { TipsPopup } from './components/modals/tips/TipsPopup';
import { Navbar } from './components/navbar/Navbar.tsx';
import { useAppDispatch, useAppSelector } from './hooks';
import { useHotjarPosition } from './hooks/useHotjarPosition';
import { AcceptInvitationPage } from './pages/accept-invitation/AcceptInvitationPage';
import { ActivationPendingPage } from './pages/activation/ActivationPendingPage';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
import { AuditLogsPage } from './pages/audit-logs/AuditLogsPage';
import { CatalogueViewPage } from './pages/catalogue/CatalogueViewPage.tsx';
import { ChangePasswordPage } from './pages/change-password/ChangePasswordPage';
import { CopyProjectPage } from './pages/copy-project/CopyProjectPage';
import { CreditsPage } from './pages/credits/CreditsPage';
import { DrawingEditorPage } from './pages/drawings/DrawingEditorPage';
import { DrawingsPage } from './pages/drawings/DrawingsPage';
import { EmailSubscriptionsPage } from './pages/email-subscriptions/EmailSubscriptionsPage';
import { SendEmailPage } from './pages/email-subscriptions/SendEmailPage';
import { FeatureFlagsPage } from './pages/feature-flags/FeatureFlagsPage';
import { ForbiddenPage } from './pages/forbidden/ForbiddenPage';
import { ImpersonatePage } from './pages/impersonate/ImpersonatePage';
import { InvitationPage } from './pages/invitation/InvitationPage';
import { LoginPage } from './pages/login/LoginPage';
import { NewProjectPage } from './pages/new-project/NewProjectPage.tsx';
import { NewsletterUnsubscribePage } from './pages/newsletter/NewsletterUnsubscribePage';
import { NotFoundPage } from './pages/not-found/NotFoundPage';
import { ProfilePage } from './pages/profile/ProfilePage.tsx';
import { ProjectsPage } from './pages/projects/ProjectsPage.tsx';
import { RegisterPage } from './pages/register/RegisterPage';
import { ResetPasswordPage } from './pages/reset-password/ResetPasswordPage';
import { ServiceUnavailablePage } from './pages/service-unavailable/ServiceUnavailablePage';
import { SubscriptionsPage } from './pages/subscriptions/SubscriptionsPage';
import { VerifyEmailPage } from './pages/verify-email/VerifyEmailPage';
import { WorkspacePage } from './pages/workspace/WorkspacePage.tsx';
import { featureFlagsReducer } from './store/reducers/featureFlags.reducer';
import { userReducer } from './store/reducers/user.reducer';
import { subscribeToWebSocket, unsubscribeFromWebSocket } from './store/sagas/webSocket.saga.ts';
import { getIsLoadingUserFeatureFlags } from './store/selectors/featureFlags.selector';
import { getIsRedirecting } from './store/selectors/ui.selector.ts';
import { getIsLoadingUser, getUser } from './store/selectors/user.selector.ts';
import { TOAST_AUTO_CLOSE_DURATION, theme } from './theme';
import { isPublic } from './utils/authUtils';

const { loadUser } = userReducer.actions;
const { loadUserFeatureFlags } = featureFlagsReducer.actions;

const AUTH_ROUTES = new Set([
  '/login',
  '/register',
  '/activation-pending',
  '/reset-password',
  '/change-password',
  '/verify-email',
  '/newsletter/unsubscribe',
  '/accept-invitation',
]);

export const App = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isLoadingUser = useAppSelector(getIsLoadingUser);

  const isLoadingUserFeatureFlags = useAppSelector(getIsLoadingUserFeatureFlags);
  const isRedirecting = useAppSelector(getIsRedirecting);
  const isPublicPage = isPublic(location.pathname);
  const isAuthPage = AUTH_ROUTES.has(location.pathname);
  const isLoading = !isPublicPage && (isLoadingUser || isLoadingUserFeatureFlags || isRedirecting);

  useHotjarPosition(isAuthPage);

  // Load user data when app starts
  useEffect(() => {
    if (!isPublicPage) {
      dispatch(loadUser());
      dispatch(loadUserFeatureFlags());
      dispatch(subscribeToWebSocket());
      return () => {
        dispatch(unsubscribeFromWebSocket());
      };
    }

    dispatch(unsubscribeFromWebSocket());
    return () => {
      dispatch(unsubscribeFromWebSocket());
    };
  }, [dispatch, isPublicPage]);

  useEffect(() => {
    if (user?.settings?.preferredLanguage && user.settings.preferredLanguage !== i18n.language) {
      i18n.changeLanguage(user.settings.preferredLanguage);
    }
  }, [user?.settings?.preferredLanguage, i18n]);

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingSpinner fullScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position="top-right"
        autoClose={TOAST_AUTO_CLOSE_DURATION}
        hideProgressBar
        closeOnClick={false}
        pauseOnHover
        toastStyle={{
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
        }}
      />
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {!isAuthPage && <Navbar />}
        {!isAuthPage && <PaywallModal />}
        {!isAuthPage && <TutorialsModal />}
        {!isAuthPage && <TipsPopup />}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="*" element={<NotFoundPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />
            <Route path="/service-unavailable" element={<ServiceUnavailablePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/activation-pending" element={<ActivationPendingPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/catalogue" element={<CatalogueViewPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/new-project" element={<NewProjectPage />} />
            <Route path="/drawings" element={<DrawingsPage />} />
            <Route path="/drawings/:id" element={<DrawingEditorPage />} />
            <Route path="/workspace" element={<WorkspacePage />} />
            <Route element={<AdminLayout />}>
              <Route path="/impersonate" element={<ImpersonatePage />} />
              <Route path="/invitation" element={<InvitationPage />} />
              <Route path="/credits" element={<CreditsPage />} />
              <Route path="/copy-project" element={<CopyProjectPage />} />
              <Route path="/email-subscriptions" element={<EmailSubscriptionsPage />} />
              <Route path="/email-subscriptions/send" element={<SendEmailPage />} />
              <Route path="/feature-flags" element={<FeatureFlagsPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/audit-logs" element={<AuditLogsPage />} />
            </Route>
          </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
